import { useEffect, useState } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import useLocalization from "../../hooks/useLocalization";
import useData from "../../hooks/useData";

import Input from "../elements/Input";
import Switch from "../elements/Switch";
import EditorToolbar from "./EditorToolbar";
import Availability from "../ui/Availability";
import Select from "../elements/Select";
import Logic from "../ui/Logic";
import MatrixItem from "../ui/MatrixItem";
import SkuMatrix from "../ui/SkuMatrix";
import Visibility from "../ui/Visibility";
import RichTextEditorModal from "../modals/RichTextEditorModal";
import Variability from "../ui/Variability";
import DescriptionImageGallery from "../ui/DescriptionImageGallery";

const sectionSchema = {
  id: null,
  surveyId: null,
  surveyPageId: null,
  name: "",
  name_fr: "",
  sectionNumber: null,
  isActive: true,
  availability: [],
  visibility: [],
  controlledBy: null,
  sectionType: "question",
  items: null,
  text: null,
  text_fr: null,
};

const SurveySectionEditor = ({
  surveyId,
  surveyPageId,
  section,
  onSave,
  onDelete,
  changesMade,
  canDelete = false,
}) => {
  const { getLabel, sectionTypes, getTitle } = useLocalization();
  const { getFromServer, updateRecord, deleteRecord } = useData();

  const [sectionData, setSectionData] = useState(null);
  const [sections, setSections] = useState([]);
  const [showTextEditor, setShowTextEditor] = useState(false);
  const [showTextEditorFr, setShowTextEditorFr] = useState(false);
  const [showDescriptionEditor, setShowDescriptionEditor] = useState(false);
  const [showDescriptionEditorFr, setShowDescriptionEditorFr] = useState(false);

  useEffect(() => {
    getSections();
  }, [section]);

  const getSections = async () => {
    const sectionList = await getFromServer({
      entityName: "SurveySection",
      filter: { surveyPageId },
    });

    if (section) {
      setSectionData(section);
    } else {
      setSectionData({
        ...sectionSchema,
        surveyId,
        surveyPageId,
        sectionNumber: Array.isArray(sectionList) ? sectionList.length : 0,
      });
    }

    setSections(sectionList);
  };

  const handleChange = async (field, newValue) => {
    if (field === "variability" && !section?.id) {
      await handleSaveOnly();
    }

    setSectionData((current) => {
      return { ...current, [field]: newValue };
    });
    changesMade(true);
  };

  const handleSave = async () => {
    await updateRecord("SurveySection", sectionData);
    onSave();
  };

  const handleSaveOnly = async () => {
    await updateRecord("SurveySection", sectionData);
  };

  const handleDelete = async () => {
    if (section?.id) {
      await deleteRecord("SurveySection", section.id);
      if (onDelete) {
        onDelete();
      } else {
        onSave();
      }
    }
  };

  if (!sectionData) return null;

  return (
    <>
      <Box
        width="100%"
        height="100%"
      >
        <EditorToolbar
          onSave={handleSave}
          onDelete={canDelete ? handleDelete : null}
        />
        <Stack
          spacing={1}
          padding="10px"
          height="calc(100% - 60px)"
          width="calc(100% - 20px)"
          overflow="auto"
        >
          <Input
            id="name"
            label={getLabel("name")}
            value={sectionData.name}
            onChange={(newValue) => handleChange("name", newValue)}
            required
            fullwidth
            multiline
          />
          <Input
            id="name_fr"
            label={getLabel("name_fr")}
            value={sectionData.name_fr}
            onChange={(newValue) => handleChange("name_fr", newValue)}
            required
            fullwidth
            multiline
          />
          <Box
            display="flex"
            justifyContent="space-between"
            gap={2}
          >
            <Select
              id="sectionType"
              label={getLabel("type")}
              value={sectionData.sectionType}
              onChange={(newValue) => handleChange("sectionType", newValue)}
              items={sectionTypes}
              disabled={Boolean(sectionData?.id || sectionData?._id)}
            />
            <Switch
              label={getLabel("active")}
              value={sectionData.isActive}
              onChange={(newValue) =>
                handleChange("isActive", Boolean(newValue))
              }
            />
          </Box>

          {sectionData?.sectionType === "sku" ? (
            <SkuMatrix
              surveyId={surveyId}
              skuMatrix={sectionData?.skuMatrix}
              onChange={(newValue) => handleChange("skuMatrix", newValue)}
              section={section}
            />
          ) : null}

          <Logic
            entityName="SurveySection"
            surveyId={surveyId}
            section={sectionData}
            value={sectionData.controlledBy}
            onChange={(newValue) => handleChange("controlledBy", newValue)}
          />

          <Availability
            value={sectionData.availability}
            onChange={(newValue) => handleChange("availability", newValue)}
            label={getLabel("availability")}
          />

          <Visibility
            label={getLabel("visibility")}
            value={sectionData?.visibility}
            onChange={(newValue) => handleChange("visibility", newValue)}
          />

          {/* Description Section */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: "bold" }}>
                Description (Optional)
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                {/* Description Text - English */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1 }}
                  >
                    Description Text (English)
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: "3px",
                      padding: "10px",
                      cursor: "pointer",
                      minHeight: "40px",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                    }}
                    onClick={() => setShowDescriptionEditor(true)}
                  >
                    <Box
                      sx={{
                        fontSize: "12px",
                        color: "#666",
                        "& p": { margin: 0 },
                      }}
                      dangerouslySetInnerHTML={{
                        __html:
                          sectionData?.descriptionText?.trim().length > 0
                            ? sectionData.descriptionText
                            : "<em>Click to add description text...</em>",
                      }}
                    />
                  </Box>
                </Box>

                {/* Description Images - English */}
                <DescriptionImageGallery
                  entityType="surveysection"
                  entityId={sectionData?.id}
                  images={sectionData?.descriptionImages || []}
                  images_fr={sectionData?.descriptionImages_fr || []}
                  onChange={(newValue) => {
                    handleChange("descriptionImages", newValue.images);
                    handleChange("descriptionImages_fr", newValue.images_fr);
                  }}
                  language="en"
                />

                {/* Description Text - French */}
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1 }}
                  >
                    Description Text (French)
                  </Typography>
                  <Box
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: "3px",
                      padding: "10px",
                      cursor: "pointer",
                      minHeight: "40px",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                    }}
                    onClick={() => setShowDescriptionEditorFr(true)}
                  >
                    <Box
                      sx={{
                        fontSize: "12px",
                        color: "#666",
                        "& p": { margin: 0 },
                      }}
                      dangerouslySetInnerHTML={{
                        __html:
                          sectionData?.descriptionText_fr?.trim().length > 0
                            ? sectionData.descriptionText_fr
                            : "<em>Cliquez pour ajouter une description...</em>",
                      }}
                    />
                  </Box>
                </Box>

                {/* Description Images - French */}
                <DescriptionImageGallery
                  entityType="surveysection"
                  entityId={sectionData?.id}
                  images={sectionData?.descriptionImages || []}
                  images_fr={sectionData?.descriptionImages_fr || []}
                  onChange={(newValue) => {
                    handleChange("descriptionImages", newValue.images);
                    handleChange("descriptionImages_fr", newValue.images_fr);
                  }}
                  language="fr"
                />
              </Stack>
            </AccordionDetails>
          </Accordion>

          <Variability
            surveySectionId={sectionData.id}
            variability={sectionData?.variability || null}
            onChange={(newValue) => handleChange("variability", newValue)}
          />

          {sectionData?.sectionType === "matrix" ? (
            <MatrixItem
              surveyId={surveyId}
              matrixItems={sectionData?.matrixItems}
              onChange={(newValue) => handleChange("matrixItems", newValue)}
            />
          ) : null}

          {sectionData?.sectionType === "text" ? (
            <Box>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Section Text (English)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: "3px",
                      padding: "10px",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                    onClick={() => setShowTextEditor(true)}
                  >
                    <Box
                      sx={{
                        fontSize: "12px",
                        color: "#666",
                        minHeight: "40px",
                        wordBreak: "break-word",
                        "& p": { margin: 0 },
                      }}
                      // Rendering HTML preview; content originates from our editor
                      dangerouslySetInnerHTML={{
                        __html:
                          sectionData?.text &&
                          sectionData.text.trim().length > 0
                            ? sectionData.text
                            : "<em>Click to edit English text...</em>",
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: "bold" }}>
                    Texte de section (French)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{
                      border: "1px solid #ccc",
                      borderRadius: "3px",
                      padding: "10px",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                    }}
                    onClick={() => setShowTextEditorFr(true)}
                  >
                    <Box
                      sx={{
                        fontSize: "12px",
                        color: "#666",
                        minHeight: "40px",
                        wordBreak: "break-word",
                        "& p": { margin: 0 },
                      }}
                      // Rendering HTML preview; content originates from our editor
                      dangerouslySetInnerHTML={{
                        __html:
                          sectionData?.text_fr &&
                          sectionData.text_fr.trim().length > 0
                            ? sectionData.text_fr
                            : "<em>Cliquez pour modifier le texte français...</em>",
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          ) : null}
        </Stack>
      </Box>

      <RichTextEditorModal
        html={sectionData?.text || ""}
        open={showTextEditor}
        onClose={() => setShowTextEditor(false)}
        onChange={(newValue) => handleChange("text", newValue)}
      />

      <RichTextEditorModal
        html={sectionData?.text_fr || ""}
        open={showTextEditorFr}
        onClose={() => setShowTextEditorFr(false)}
        onChange={(newValue) => handleChange("text_fr", newValue)}
      />

      <RichTextEditorModal
        html={sectionData?.descriptionText || ""}
        open={showDescriptionEditor}
        onClose={() => setShowDescriptionEditor(false)}
        onChange={(newValue) => handleChange("descriptionText", newValue)}
      />

      <RichTextEditorModal
        html={sectionData?.descriptionText_fr || ""}
        open={showDescriptionEditorFr}
        onClose={() => setShowDescriptionEditorFr(false)}
        onChange={(newValue) => handleChange("descriptionText_fr", newValue)}
      />
    </>
  );
};

export default SurveySectionEditor;
